import { withIdentity } from "flavours/glitch/identity_context";
import PropTypes from "prop-types";

import { defineMessages, injectIntl } from "react-intl";

import classNames from "classnames";

import ImmutablePropTypes from "react-immutable-proptypes";
import ImmutablePureComponent from "react-immutable-pure-component";
import { connect } from "react-redux";

import { followAccount } from "flavours/glitch/actions/accounts";
import { followHashtag, unfollowHashtag } from "flavours/glitch/actions/tags";

import DropdownMenuContainer from "flavours/glitch/containers/dropdown_menu_container";
import { me } from "flavours/glitch/initial_state";
import { PERMISSION_MANAGE_USERS, PERMISSION_MANAGE_FEDERATION } from "flavours/glitch/permissions";
import { accountAdminLink, statusAdminLink } from "flavours/glitch/utils/backend_links";
import { prefetchFollowedTags, prefetchRelationship } from "flavours/glitch/utils/menu_prefetch";

import { IconButton } from "./icon_button";
import { RelativeTimestamp } from "./relative_timestamp";

const messages = defineMessages({
  delete: { id: "status.delete", defaultMessage: "Delete" },
  redraft: { id: "status.redraft", defaultMessage: "Delete & re-draft" },
  edit: { id: "status.edit", defaultMessage: "Edit" },
  direct: { id: "status.direct", defaultMessage: "Privately mention @{name}" },
  mention: { id: "status.mention", defaultMessage: "Mention @{name}" },
  mute: { id: "account.mute", defaultMessage: "Mute @{name}" },
  block: { id: "account.block", defaultMessage: "Block @{name}" },
  reply: { id: "status.reply", defaultMessage: "Reply" },
  share: { id: "status.share", defaultMessage: "Share" },
  more: { id: "status.more", defaultMessage: "More" },
  replyAll: { id: "status.replyAll", defaultMessage: "Reply to thread" },
  reblog: { id: "status.reblog", defaultMessage: "Boost" },
  reblog_private: { id: "status.reblog_private", defaultMessage: "Boost with original visibility" },
  cancel_reblog_private: { id: "status.cancel_reblog_private", defaultMessage: "Unboost" },
  cannot_reblog: { id: "status.cannot_reblog", defaultMessage: "This post cannot be boosted" },
  favourite: { id: "status.favourite", defaultMessage: "Favorite" },
  bookmark: { id: "status.bookmark", defaultMessage: "Bookmark" },
  open: { id: "status.open", defaultMessage: "Expand this status" },
  report: { id: "status.report", defaultMessage: "Report @{name}" },
  muteConversation: { id: "status.mute_conversation", defaultMessage: "Mute conversation" },
  unmuteConversation: { id: "status.unmute_conversation", defaultMessage: "Unmute conversation" },
  pin: { id: "status.pin", defaultMessage: "Pin on profile" },
  unpin: { id: "status.unpin", defaultMessage: "Unpin from profile" },
  embed: { id: "status.embed", defaultMessage: "Embed" },
  admin_account: { id: "status.admin_account", defaultMessage: "Open moderation interface for @{name}" },
  admin_status: { id: "status.admin_status", defaultMessage: "Open this post in the moderation interface" },
  admin_domain: { id: "status.admin_domain", defaultMessage: "Open moderation interface for {domain}" },
  copy: { id: "status.copy", defaultMessage: "Copy link to post" },
  hide: { id: "status.hide", defaultMessage: "Hide post" },
  edited: { id: "status.edited", defaultMessage: "Edited {date}" },
  filter: { id: "status.filter", defaultMessage: "Filter this post" },
  openOriginalPage: { id: "account.open_original_page", defaultMessage: "Open original page" },
  hideReblogs: { id: "account.hide_reblogs", defaultMessage: "Hide boosts from @{name}" },
  showReblogs: { id: "account.show_reblogs", defaultMessage: "Show boosts from @{name}" },
  followTag: { id: "status.follow_tag", defaultMessage: "Follow #{name}" },
  unfollowTag: { id: "status.unfollow_tag", defaultMessage: "Unfollow #{name}" },
});

// More hashtags would make the menu long; the first ones are usually the topic.
const MAX_MENU_TAGS = 4;

// The account whose boosts the menu can hide: the booster of a boost, otherwise
// the author (not for own posts).
const boostAccount = (status, rebloggedBy) => {
  if (rebloggedBy) {
    return rebloggedBy.get("id") === me ? null : rebloggedBy;
  }

  return status.getIn(["account", "id"]) === me ? null : status.get("account");
};

class StatusActionBar extends ImmutablePureComponent {

  static contextTypes = {
    router: PropTypes.object,
  };

  static propTypes = {
    status: ImmutablePropTypes.map.isRequired,
    onReply: PropTypes.func,
    onFavourite: PropTypes.func,
    onReblog: PropTypes.func,
    onDelete: PropTypes.func,
    onDirect: PropTypes.func,
    onMention: PropTypes.func,
    onMute: PropTypes.func,
    onBlock: PropTypes.func,
    onReport: PropTypes.func,
    onEmbed: PropTypes.func,
    onMuteConversation: PropTypes.func,
    onPin: PropTypes.func,
    onBookmark: PropTypes.func,
    onFilter: PropTypes.func,
    onAddFilter: PropTypes.func,
    onInteractionModal: PropTypes.func,
    rebloggedBy: ImmutablePropTypes.map,
    relationship: ImmutablePropTypes.map,
    followedTags: ImmutablePropTypes.list,
    tagState: ImmutablePropTypes.map,
    onReblogsToggle: PropTypes.func,
    onTagToggle: PropTypes.func,
    onPrefetch: PropTypes.func,
    withDismiss: PropTypes.bool,
    withCounters: PropTypes.bool,
    showReplyCount: PropTypes.bool,
    scrollKey: PropTypes.string,
    intl: PropTypes.object.isRequired,
  };

  // Avoid checking props that are functions (and whose equality will always
  // evaluate to false. See react-immutable-pure-component for usage.
  updateOnProps = [
    "status",
    "showReplyCount",
    "withCounters",
    "withDismiss",
    "rebloggedBy",
    "relationship",
    "followedTags",
    "tagState",
  ];

  componentDidMount () {
    if (this.props.identity.signedIn && this.props.onPrefetch) {
      this.props.onPrefetch(boostAccount(this.props.status, this.props.rebloggedBy), this.props.status.get("tags")?.size > 0);
    }
  }

  isFollowingTag (name) {
    const { tagState, followedTags } = this.props;
    const known = tagState?.getIn([name, "following"]);

    if (known !== undefined) {
      return known;
    }

    const lower = name.toLowerCase();
    return !!followedTags?.some(tag => tag.get("name", "").toLowerCase() === lower);
  }

  handleReblogsToggle = () => {
    const { status, rebloggedBy, relationship } = this.props;
    this.props.onReblogsToggle(boostAccount(status, rebloggedBy), relationship.get("showing_reblogs"));
  };

  handleReplyClick = () => {
    const { signedIn } = this.props.identity;

    if (signedIn) {
      this.props.onReply(this.props.status, this.context.router.history);
    } else {
      this.props.onInteractionModal("reply", this.props.status);
    }
  };

  handleShareClick = () => {
    navigator.share({
      url: this.props.status.get("url"),
    });
  };

  handleFavouriteClick = (e) => {
    const { signedIn } = this.props.identity;

    if (signedIn) {
      this.props.onFavourite(this.props.status, e);
    } else {
      this.props.onInteractionModal("favourite", this.props.status);
    }
  };

  handleReblogClick = e => {
    const { signedIn } = this.props.identity;

    if (signedIn) {
      this.props.onReblog(this.props.status, e);
    } else {
      this.props.onInteractionModal("reblog", this.props.status);
    }
  };

  handleBookmarkClick = (e) => {
    this.props.onBookmark(this.props.status, e);
  };

  handleDeleteClick = () => {
    this.props.onDelete(this.props.status, this.context.router.history);
  };

  handleRedraftClick = () => {
    this.props.onDelete(this.props.status, this.context.router.history, true);
  };

  handleEditClick = () => {
    this.props.onEdit(this.props.status, this.context.router.history);
  };

  handlePinClick = () => {
    this.props.onPin(this.props.status);
  };

  handleMentionClick = () => {
    this.props.onMention(this.props.status.get("account"), this.context.router.history);
  };

  handleDirectClick = () => {
    this.props.onDirect(this.props.status.get("account"), this.context.router.history);
  };

  handleMuteClick = () => {
    this.props.onMute(this.props.status.get("account"));
  };

  handleBlockClick = () => {
    this.props.onBlock(this.props.status);
  };

  handleOpen = () => {
    let state = { ...this.context.router.history.location.state };
    if (state.mastodonModalKey) {
      this.context.router.history.replace(`/@${this.props.status.getIn(["account", "acct"])}/${this.props.status.get("id")}`);
    } else {
      this.context.router.history.push(`/@${this.props.status.getIn(["account", "acct"])}/${this.props.status.get("id")}`);
    }
  };

  handleEmbed = () => {
    this.props.onEmbed(this.props.status);
  };

  handleReport = () => {
    this.props.onReport(this.props.status);
  };

  handleConversationMuteClick = () => {
    this.props.onMuteConversation(this.props.status);
  };

  handleCopy = () => {
    const url = this.props.status.get("url");
    navigator.clipboard.writeText(url);
  };

  handleHideClick = () => {
    this.props.onFilter();
  };

  handleFilterClick = () => {
    this.props.onAddFilter(this.props.status);
  };

  render () {
    const { status, intl, withDismiss, withCounters, showReplyCount, scrollKey } = this.props;
    const { permissions, signedIn } = this.props.identity;

    const mutingConversation = status.get("muted");
    const publicStatus       = ["public", "unlisted"].includes(status.get("visibility"));
    const pinnableStatus     = ["public", "unlisted", "private"].includes(status.get("visibility"));
    const writtenByMe        = status.getIn(["account", "id"]) === me;
    const isRemote           = status.getIn(["account", "username"]) !== status.getIn(["account", "acct"]);

    let menu = [];
    let replyIcon;
    let replyTitle;

    menu.push({ text: intl.formatMessage(messages.open), action: this.handleOpen });

    if (publicStatus && isRemote) {
      menu.push({ text: intl.formatMessage(messages.openOriginalPage), href: status.get("url") });
    }

    menu.push({ text: intl.formatMessage(messages.copy), action: this.handleCopy });

    if (publicStatus && "share" in navigator) {
      menu.push({ text: intl.formatMessage(messages.share), action: this.handleShareClick });
    }

    if (publicStatus && (signedIn || !isRemote)) {
      menu.push({ text: intl.formatMessage(messages.embed), action: this.handleEmbed });
    }

    if (signedIn) {
      menu.push(null);

      const target = boostAccount(status, this.props.rebloggedBy);
      const relationship = this.props.relationship;
      const tags = status.get("tags", []).slice(0, MAX_MENU_TAGS);
      let followGroup = false;

      // Only for accounts I follow: the follow request that changes the setting
      // would otherwise follow the account.
      if (target && relationship?.get("following")) {
        const showing = relationship.get("showing_reblogs");
        menu.push({ text: intl.formatMessage(showing ? messages.hideReblogs : messages.showReblogs, { name: target.get("username") }), action: this.handleReblogsToggle });
        followGroup = true;
      }

      tags.forEach(tag => {
        const name = tag.get("name");
        const following = this.isFollowingTag(name);
        menu.push({ text: intl.formatMessage(following ? messages.unfollowTag : messages.followTag, { name }), action: () => this.props.onTagToggle(name, following) });
        followGroup = true;
      });

      if (followGroup) {
        menu.push(null);
      }

      if (writtenByMe && pinnableStatus) {
        menu.push({ text: intl.formatMessage(status.get("pinned") ? messages.unpin : messages.pin), action: this.handlePinClick });
        menu.push(null);
      }

      if (writtenByMe || withDismiss) {
        menu.push({ text: intl.formatMessage(mutingConversation ? messages.unmuteConversation : messages.muteConversation), action: this.handleConversationMuteClick });
        menu.push(null);
      }

      if (writtenByMe) {
        menu.push({ text: intl.formatMessage(messages.edit), action: this.handleEditClick });
        menu.push({ text: intl.formatMessage(messages.delete), action: this.handleDeleteClick, dangerous: true });
        menu.push({ text: intl.formatMessage(messages.redraft), action: this.handleRedraftClick, dangerous: true });
      } else {
        menu.push({ text: intl.formatMessage(messages.mention, { name: status.getIn(["account", "username"]) }), action: this.handleMentionClick });
        menu.push({ text: intl.formatMessage(messages.direct, { name: status.getIn(["account", "username"]) }), action: this.handleDirectClick });
        menu.push(null);

        if (!this.props.onFilter) {
          menu.push({ text: intl.formatMessage(messages.filter), action: this.handleFilterClick, dangerous: true });
          menu.push(null);
        }

        menu.push({ text: intl.formatMessage(messages.mute, { name: status.getIn(["account", "username"]) }), action: this.handleMuteClick, dangerous: true });
        menu.push({ text: intl.formatMessage(messages.block, { name: status.getIn(["account", "username"]) }), action: this.handleBlockClick, dangerous: true });
        menu.push({ text: intl.formatMessage(messages.report, { name: status.getIn(["account", "username"]) }), action: this.handleReport, dangerous: true });

        if (((permissions & PERMISSION_MANAGE_USERS) === PERMISSION_MANAGE_USERS && (accountAdminLink || statusAdminLink)) || (isRemote && (permissions & PERMISSION_MANAGE_FEDERATION) === PERMISSION_MANAGE_FEDERATION)) {
          menu.push(null);
          if ((permissions & PERMISSION_MANAGE_USERS) === PERMISSION_MANAGE_USERS) {
            if (accountAdminLink !== undefined) {
              menu.push({ text: intl.formatMessage(messages.admin_account, { name: status.getIn(["account", "username"]) }), href: accountAdminLink(status.getIn(["account", "id"])) });
            }
            if (statusAdminLink !== undefined) {
              menu.push({ text: intl.formatMessage(messages.admin_status), href: statusAdminLink(status.getIn(["account", "id"]), status.get("id")) });
            }
          }
          if (isRemote && (permissions & PERMISSION_MANAGE_FEDERATION) === PERMISSION_MANAGE_FEDERATION) {
            const domain = status.getIn(["account", "acct"]).split("@")[1];
            menu.push({ text: intl.formatMessage(messages.admin_domain, { domain: domain }), href: `/admin/instances/${domain}` });
          }
        }
      }
    }

    if (status.get("in_reply_to_id", null) === null) {
      replyIcon = "arrow-bend-up-left";
      replyTitle = intl.formatMessage(messages.reply);
    } else {
      replyIcon = "arrow-bend-double-up-left";
      replyTitle = intl.formatMessage(messages.replyAll);
    }

    const reblogPrivate = status.getIn(["account", "id"]) === me && status.get("visibility") === "private";

    let reblogTitle = "";
    if (status.get("reblogged")) {
      reblogTitle = intl.formatMessage(messages.cancel_reblog_private);
    } else if (publicStatus) {
      reblogTitle = intl.formatMessage(messages.reblog);
    } else if (reblogPrivate) {
      reblogTitle = intl.formatMessage(messages.reblog_private);
    } else {
      reblogTitle = intl.formatMessage(messages.cannot_reblog);
    }

    const filterButton = this.props.onFilter && (
      <IconButton className='status__action-bar-button' title={intl.formatMessage(messages.hide)} icon='eye' onClick={this.handleHideClick} />
    );

    return (
      <div className='status__action-bar'>
        <IconButton
          className='status__action-bar-button'
          title={replyTitle}
          icon={replyIcon}
          onClick={this.handleReplyClick}
          counter={showReplyCount ? status.get("replies_count") : undefined}
          obfuscateCount
        />
        <IconButton className={classNames("status__action-bar-button status-boost", { reblogPrivate })} disabled={!publicStatus && !reblogPrivate} active={status.get("reblogged")} title={reblogTitle} icon={status.get("reblogged") ? "rocket-launch-filled" : "rocket-launch"} onClick={this.handleReblogClick} counter={withCounters ? status.get("reblogs_count") : undefined} />
        <IconButton className='status__action-bar-button star-icon status-favourite' animate active={status.get("favourited")} title={intl.formatMessage(messages.favourite)} icon={status.get("favourited") ? "star-filled" : "star"} onClick={this.handleFavouriteClick} counter={withCounters ? status.get("favourites_count") : undefined} />
        <IconButton className='status__action-bar-button bookmark-icon status-bookmark' disabled={!signedIn} active={status.get("bookmarked")} title={intl.formatMessage(messages.bookmark)} icon={status.get("bookmarked") ? "bookmark-filled" : "bookmark"} onClick={this.handleBookmarkClick} />

        {filterButton}

        <div className='status__action-bar-dropdown'>
          <DropdownMenuContainer
            scrollKey={scrollKey}
            status={status}
            items={menu}
            icon='dots-three-outline'
            size={18}
            direction='right'
            ariaLabel={intl.formatMessage(messages.more)}
          />
        </div>

        <div className='status__action-bar-spacer' />
        <a href={status.get("url")} className='status__relative-time' target='_blank' rel="noopener noreferrer">
          <RelativeTimestamp timestamp={status.get("created_at")} />{status.get("edited_at") && <abbr title={intl.formatMessage(messages.edited, { date: intl.formatDate(status.get("edited_at"), { hour12: false, year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) })}> *</abbr>}
        </a>
      </div>
    );
  }

}

const makeMapStateToProps = () => (state, { status, rebloggedBy }) => {
  const target = boostAccount(status, rebloggedBy);

  return {
    relationship: target ? state.getIn(["relationships", target.get("id")]) : null,
    followedTags: state.getIn(["followed_tags", "items"]),
    tagState: state.get("tags"),
  };
};

const mapDispatchToProps = dispatch => ({
  onReblogsToggle (account, showing) {
    dispatch(followAccount(account.get("id"), { reblogs: !showing }));
  },

  onTagToggle (name, following) {
    dispatch(following ? unfollowHashtag(name) : followHashtag(name));
  },

  onPrefetch (account, hasTags) {
    if (account) {
      prefetchRelationship(dispatch, account.get("id"));
    }
    if (hasTags) {
      prefetchFollowedTags(dispatch);
    }
  },
});

export default connect(makeMapStateToProps, mapDispatchToProps)(withIdentity(injectIntl(StatusActionBar)));
